export const META_PIXEL_ID = "743072714970606";

const SCRIPT_SRC = "https://connect.facebook.net/en_US/fbevents.js";

type MetaPixelCall = readonly unknown[];

/** Shape of Meta's official stub; fbevents.js fills `callMethod`. */
export interface MetaPixelQueue {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  readonly queue: MetaPixelCall[];
  push?: MetaPixelQueue;
  readonly loaded: boolean;
  readonly version: string;
  /** We fire route PageViews ourselves; blocks fbevents' history auto-PageView. */
  readonly disablePushState: boolean;
  /** fbevents keeps one PageView per document otherwise; dedupe is `trackMetaPageView`. */
  readonly allowDuplicatePageViews: boolean;
}

declare global {
  interface Window {
    fbq?: MetaPixelQueue;
    _fbq?: MetaPixelQueue;
  }
}

const pixel = {
  loaded: false,
  granted: false,
  lastPageViewPath: null as string | null,
};

function createQueue(): MetaPixelQueue {
  const calls: MetaPixelCall[] = [];
  const queue: MetaPixelQueue = Object.assign(
    (...args: unknown[]): void => {
      if (queue.callMethod) queue.callMethod.apply(queue, args);
      else calls.push(args);
    },
    {
      queue: calls,
      loaded: true,
      version: "2.0",
      disablePushState: true,
      allowDuplicatePageViews: true,
    },
  );
  queue.push = queue;
  return queue;
}

function insertScript(): void {
  const script = document.createElement("script");
  script.async = true;
  script.src = SCRIPT_SRC;
  document.head.appendChild(script);
}

/** Loads fbevents.js on first grant only. Idempotent. */
export function grantMetaPixel(): void {
  if (typeof window === "undefined" || pixel.granted) return;
  if (pixel.loaded) {
    window.fbq?.("consent", "grant");
    pixel.granted = true;
    return;
  }
  const existing: MetaPixelQueue | undefined = window.fbq;
  const fbq: MetaPixelQueue = existing ?? createQueue();
  if (existing === undefined) {
    window.fbq = fbq;
    window._fbq = window._fbq ?? fbq;
    insertScript();
  }
  // Auto events would guess "Lead" from button clicks: only our success path sends it.
  fbq("set", "autoConfig", false, META_PIXEL_ID);
  fbq("init", META_PIXEL_ID);
  pixel.loaded = true;
  pixel.granted = true;
}

/** Holds every further event; cookies go via `deleteUnconsentedCookies`. */
export function revokeMetaPixel(): void {
  if (typeof window === "undefined") return;
  if (pixel.granted) window.fbq?.("consent", "revoke");
  pixel.granted = false;
  pixel.lastPageViewPath = null;
}

/** fbevents.js was injected in this document: its listeners outlive a revoke. */
export function metaPixelLoaded(): boolean {
  return pixel.loaded;
}

/** Same path twice in a row = Strict Mode / re-render, not a page view. */
export function trackMetaPageView(path: string): void {
  if (!pixel.granted || pixel.lastPageViewPath === path) return;
  pixel.lastPageViewPath = path;
  window.fbq?.("track", "PageView");
}

/** Call only after the lead is stored server-side. */
export function trackMetaLead(): void {
  if (!pixel.granted) return;
  window.fbq?.("track", "Lead");
}
